import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../../assets/icons/clock-v2-icon.svg';
import { ReactComponent as CreditCardIcon } from '../../../assets/icons/credit-card.svg';
import { ReactComponent as CurrencyIcon } from '../../../assets/icons/currentPrice.svg';
import { ReactComponent as GlobeIcon } from '../../../assets/icons/globe-icon.svg';
import { ReactComponent as ApplePayLogo } from '../../../assets/icons/payment-apple.svg';
import { ReactComponent as MastercardLogo } from '../../../assets/icons/payment-mastercard.svg';
import { ReactComponent as PayPalLogo } from '../../../assets/icons/payment-paypal.svg';
import { ReactComponent as StripeLogo } from '../../../assets/icons/payment-stripe.svg';
import { ReactComponent as VisaLogo } from '../../../assets/icons/payment-visa.svg';
import { ReactComponent as ReceiptIcon } from '../../../assets/icons/receipt-icon.svg';
import { PortalType } from '../../../config/enum';
import { usePortal } from '../../../contexts/PortalContext/PortalContext';
import { GatewayListApi } from '../../../interface/payment-transaction-interface';
import { useAppDispatch } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import MyButton from '../../../shared-components/button/Button';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import { getCountryName } from '../../../shared-functions/CountryCodes';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import paymentGatewayApi from '../../../utils/api/v2/payment-gateway';

import AddGateway from './AddGateway';
import ViewGateway from './ViewGateway';

interface ModalInterface {
  title: string;
  content: JSX.Element;
  hideHeader: boolean;
}

/**
 * Mada logo component
 * @returns {any} React node
 */
function MadaLogoComponent() {
  return (
    <div className="mada-logo-inline method-icon" title="Mada">
      mada
    </div>
  );
}

/**
 * Payment Gateway List Component
 * @returns {any} React node
 */
function PaymentGatewayList() {
  const { t, i18n } = useTranslation('paymentGateway');
  const { activePortal } = usePortal();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [gatewayList, setGatewayList] = useState<GatewayListApi[]>([]);
  const [sidePanelStatus, setSidePanelStatus] = useState<'open' | 'close'>('close');

  const [sidePanelView, setSidePanelView] = useState<ModalInterface>({
    title: '',
    content: <div />,
    hideHeader: false,
  });

  const [successModal, setSuccessModal] = useState<{
    open: boolean;
    title: string;
    description: string;
  }>({
    open: false,
    title: '',
    description: '',
  });

  /**
   * Fetch Gateway List API
   */
  const getGatewayList = async () => {
    try {
      const response = await paymentGatewayApi.fetchPaymentGatewayConfigList();

      console.log(response);

      setGatewayList(response.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  /**
   * Handle gateway creation or update success
   * @param {'add' | 'edit'} action - The action type executed
   */
  const handleSuccess = (action: 'add' | 'edit') => {
    setSidePanelStatus('close');
    getGatewayList();
    setSuccessModal({
      open: true,
      title: t('success', 'Success'),
      description:
        action === 'edit'
          ? t('gatewayUpdatedSuccessfully', 'Payment gateway updated successfully')
          : t('gatewayAddedSuccessfully', 'Payment gateway configured successfully'),
    });
  };

  /**
   * Open add gateway side panel
   */
  const handleAddNew = () => {
    setSidePanelStatus('open');

    setSidePanelView({
      title: t('addNewGateway'),
      hideHeader: false,
      content: (
        <AddGateway
          onCancel={() => setSidePanelStatus('close')}
          onSuccess={() => handleSuccess('add')}
        />
      ),
    });
  };

  /**
   * Open view gateway details side panel
   * @param {any} gateway The gateway details
   */
  const handleViewGateway = (gateway: GatewayListApi) => {
    setSidePanelStatus('open');

    /**
     * Edit gateway d
     */
    const handleEditFlow = () => {
      setSidePanelView((previous) => ({
        ...previous,
        title: t('editGateway'),
        hideHeader: false,
        content: (
          <AddGateway
            editData={{
              configId: gateway.configId || (gateway as any)._id || (gateway as any).id,
              key: gateway.gatewayKey,
              businessType:
                gateway.useCases?.includes('b2c') && gateway.useCases?.includes('b2b')
                  ? 'both'
                  : gateway.useCases?.[0] || 'B2B',
              country: gateway.country,
              paymentMethods: gateway.paymentMethods || [],
              credentials: gateway.credentials,
              currencies: gateway.currencies || (gateway.currency ? [gateway.currency] : []),
              currency: gateway.currency,
              defaultCurrency: gateway.defaultCurrency || gateway.currency,
              isDefault: gateway.isDefault,
              platformFee: gateway.platformFee,
              platformFeeByCurrency: gateway.platformFeeByCurrency,
              platformFeeType: gateway.platformFeeType,
              paymentSurcharge: gateway.paymentSurcharge,
              paymentSurchargeType: gateway.paymentSurchargeType || 'percentage',
              mode: gateway.mode,
              status: gateway.isActive ? 'active' : 'inactive',
            }}
            onCancel={() => setSidePanelStatus('close')}
            onSuccess={() => handleSuccess('edit')}
          />
        ),
      }));
    };

    setSidePanelView({
      title: '',
      hideHeader: true,
      content: (
        <ViewGateway
          gateway={gateway}
          onEdit={handleEditFlow}
          onClose={() => setSidePanelStatus('close')}
          onStatusChange={getGatewayList}
        />
      ),
    });
  };

  /**
   * Render gateway logo
   * @param {any} name - The name
   */
  const renderGatewayLogo = (name: string) => {
    if (name.toLowerCase() === 'stripe') {
      return <StripeLogo className="gateway-logo-svg" />;
    }

    return <div className="gateway-name">{name}</div>;
  };

  /**
   * Render payment methods
   * @param {any} methods - The methods
   */
  const renderPaymentMethods = (methods: string[]) => {
    const formattedMethods = methods.map((m) => m.toLowerCase());

    const icons: React.ReactNode[] = [];

    // 1. Mada
    if (formattedMethods.includes('mada')) {
      icons.push(<MadaLogoComponent key="mada" />);
    }

    // 2. Debit card / Visa / Mastercard group
    if (
      formattedMethods.includes('debit_card') ||
      formattedMethods.includes('debit card') ||
      formattedMethods.includes('visa')
    ) {
      icons.push(
        <span key="visa" title="Visa" className="method-icon-tooltip">
          <VisaLogo className="method-icon" />
        </span>,
      );
      icons.push(
        <span key="mastercard" title="Mastercard" className="method-icon-tooltip">
          <MastercardLogo className="method-icon" />
        </span>,
      );
    }

    // 3. PayPal
    if (formattedMethods.includes('paypal')) {
      icons.push(
        <span key="paypal" title="PayPal" className="method-icon-tooltip">
          <PayPalLogo className="method-icon" />
        </span>,
      );
    }

    // 4. Apple Pay
    if (formattedMethods.includes('apple_pay') || formattedMethods.includes('apple pay')) {
      icons.push(
        <span key="apple" title="Apple Pay" className="method-icon-tooltip">
          <ApplePayLogo className="method-icon" />
        </span>,
      );
    }

    // 5. Credit Card (outline logo)
    if (formattedMethods.includes('credit_card') || formattedMethods.includes('credit card')) {
      icons.push(
        <span key="credit_card" title="Credit Card" className="method-icon-tooltip">
          <CreditCardIcon className="method-icon card-outline-icon" />
        </span>,
      );
    }

    // 6. Dynamic fallback for other payment methods without dedicated SVG icons (e.g. google_pay, stc_pay)
    const standardLogoMethods = [
      'credit_card',
      'credit card',
      'debit_card',
      'debit card',
      'mada',
      'visa',
      'paypal',
      'apple_pay',
      'apple pay',
    ];

    methods.forEach((method) => {
      const lower = method.toLowerCase();
      if (!standardLogoMethods.includes(lower)) {
        const labelMap: Record<string, string> = {
          googlePay: 'Google Pay',
          stcPay: 'STC Pay',
        };
        const label =
          labelMap[lower] ||
          method
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        const firstLetter = label.charAt(0).toUpperCase();

        icons.push(
          <div key={method} className="method-icon round-letter-logo" title={label}>
            {firstLetter}
          </div>,
        );
      }
    });

    return <div className="payment-methods-icons">{icons}</div>;
  };

  /**
   * Rendering comma separated currencies with count
   * @param {string} currencies - Passing as prop
   * @returns {React.JSX.Element | null} - returning reacts JSX element
   */
  const renderCurrencies = (currencies?: string[]): React.JSX.Element | null => {
    if (!currencies?.length) return null;
    return (
      <span>
        {currencies.slice(0, 5).join(', ')}
        {currencies.length > 5 && ` +${currencies.length - 5}`}
      </span>
    );
  };

  useEffect(() => {
    getGatewayList();
  }, [activePortal]);

  const formattedCount = String(gatewayList?.length || 0).padStart(2, '0');

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('gatewayList'),
        count: formattedCount,
      }),
    );
  }, [dispatch, formattedCount, t]);

  return (
    <div className={`payment-gateway-list-container ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      <SidePaneModel
        open={sidePanelStatus === 'open'}
        title={sidePanelView.title}
        content={sidePanelView.content}
        closePanel={() => setSidePanelStatus('close')}
        hideHeader={sidePanelView.hideHeader}
      />

      <ModalBox
        open={successModal.open}
        title=""
        content={
          <SuccessNotification
            title={successModal.title}
            description={successModal.description}
            notificationCloseTimer={3000}
            onNotificationClose={() =>
              setSuccessModal((previous) => ({ ...previous, open: false }))
            }
          />
        }
        closeModal={() => setSuccessModal((previous) => ({ ...previous, open: false }))}
        header={false}
        rightCorner=""
        notification
      />

      <div className="header-section">
        <div className="header-content">
          <h2>{t('paymentGatewayConfiguration')}</h2>

          <p>{t('paymentGatewayConfigurationDesc')}</p>
        </div>

        <div className="header-actions">
          {permissionCheck('mobilityPaymentTransactionView') && (
            <MyButton
              label={t('paymentTransactions')}
              buttonType="outline"
              onClickFunc={() => navigate('/ev-admin/configuration/PaymentGateway/transactions')}
              disableButton={
                activePortal === PortalType.NETWORKS
                  ? !permissionCheck('cpoPaymentTransactionView')
                  : !permissionCheck('mobilityPaymentTransactionView')
              }
            />
          )}
          <MyButton
            label={t('addGateway')}
            buttonType="submit"
            onClickFunc={handleAddNew}
            disableButton={
              activePortal === PortalType.NETWORKS
                ? !(
                    permissionCheck('cpoPaymentGatewayAdd') ||
                    permissionCheck('cpoPaymentGatewayUpdate')
                  )
                : !permissionCheck('mobilityPaymentGatewayAdd')
            }
          />
        </div>
      </div>

      <div className="gateway-list-wrapper">
        <div className="gateway-cards">
          {gatewayList?.map((gateway, index) => (
            <div
              className="gateway-card"
              key={index}
              onClick={() => handleViewGateway(gateway)}
              role="presentation">
              <div className="gateway-card-header">
                <div className="gateway-logo-wrapper">{renderGatewayLogo(gateway.gatewayKey)}</div>

                <div className={`status-pill ${gateway.isActive ? 'active' : 'inactive'}`}>
                  <span className="status-pill-dot" />
                  {gateway.isActive ? t('active') : t('inactive')}
                </div>
              </div>

              <div className="gateway-info-rows">
                {/* Payment Methods */}
                <div className="info-row">
                  <div className="info-label">
                    <CreditCardIcon className="info-label-icon" />
                    {t('paymentMethod')}
                  </div>

                  <div className="info-value">{renderPaymentMethods(gateway.paymentMethods)}</div>
                </div>

                {/* Country */}
                <div className="info-row">
                  <div className="info-label">
                    <GlobeIcon className="info-label-icon" />
                    {t('country')}
                  </div>

                  <div className="info-value">
                    <div className="country-tags">
                      <span>{getCountryName(gateway.country, t)}</span>
                    </div>
                  </div>
                </div>

                {/* Currency */}
                <div className="info-row">
                  <div className="info-label">
                    <CurrencyIcon className="info-label-icon" />
                    {t('currency')}
                  </div>

                  <div className="info-value">
                    <div className="country-tags">
                      {/* <span>{gateway.defaultCurrency || gateway.currency || gateway.currencies?.[0] || '--'}</span> */}
                      <span>{renderCurrencies(gateway?.currencies)}</span>
                    </div>
                  </div>
                </div>

                {/* Business Type */}
                <div className="info-row">
                  <div className="info-label">
                    <ReceiptIcon className="info-label-icon" />
                    {t('paymentFlow')}
                  </div>

                  <div className="info-value">
                    <div className="business-type-flow-row">
                      {['b2c', 'b2b'].map((type) => {
                        const isSupported = gateway.useCases?.includes(type);
                        if (!isSupported) return null;

                        const isActive =
                          Boolean(gateway.isActive) &&
                          (gateway.activeUseCases ? gateway.activeUseCases.includes(type) : true);

                        return (
                          <div key={type} className="business-status-wrap">
                            <div className="business-badge-row">
                              <div
                                className={`business-badge-v2 ${type} ${isActive ? 'active' : 'inactive'}`}>
                                <span className="badge-icon-circle">{isActive ? '✓' : ''}</span>
                                <span className="badge-text">
                                  {type.toLowerCase() === 'b2c' ? t('b2cPayment') : t('b2bPayout')}
                                </span>
                              </div>
                              <span
                                className={`active-status-text ${isActive ? 'active' : 'inactive'} ${type}`}>
                                {isActive ? t('active') : t('inactive')}
                              </span>
                            </div>
                            <span className="business-desc-text">
                              {isActive ? t('enabledForTransactions') : t('notConfigured')}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              <div className="gateway-card-footer">
                <ClockIcon className="footer-clock-icon" />
                {t('lastUpdated')}: {new Date(gateway.updatedAt).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PaymentGatewayList;
